/**
 * Policy v2 and secrets for the console: state.ext.policy (config, freezes, model switches)
 * and state.ext.secrets from the hub, kept live by SSE 'ext'; versions, diffs, effective
 * settings and launches fetched on demand. In mock mode everything runs on demo data in
 * memory, with the same pure functions the hub uses.
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type {
  EffectiveLaunch,
  FreezeWindow,
  FreezeWindowInput,
  Id,
  LaunchRecord,
  PolicyChange,
  PolicyConfig,
  PolicyDiff,
  PolicyScope,
  PolicySettings,
  PolicySimulationResult,
  PolicySnapshot,
  PolicyState,
  PolicyVersionInfo,
  SecretInput,
  SecretLease,
  SecretMeta,
  SecretsState,
} from '@orgus/protocol';
import { diffSnapshots, effectiveLaunch, freezeUntil, layerConflicts, normalizeConfig, snapshotAt, summarizeChange } from '@orgus/policy';
import type { LayerConflict } from '@orgus/policy';
import { api, apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { simulate } from '../approvals/live.ts';
import { useApprovals } from '../approvals/live.ts';
import { mockChanges, mockConfig, mockModelSwitches, mockSecrets } from './mock.ts';

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
const isMock = () => useOrgus.getState().mode === 'mock';
const policyNow = () => useOrgus.getState().ext.policy as PolicyState | undefined;
const setExt = (name: string, data: unknown) => useOrgus.getState().applyExt({ ext: name, type: 'state', data });

/** The demo's change log (mock mode only). */
let mockLog: PolicyChange[] | null = null;

function frozenOf(config: PolicyConfig, now: number) {
  return config.freezes.flatMap((window) => {
    const until = freezeUntil(window, now);
    return until == null ? [] : [{ window, until }];
  });
}

/** In mock mode, add v2 config and the secrets slice to the demo state once. */
function useMockSeedV2(): void {
  const { ready, mock, needPolicy, needSecrets, hasPolicy } = useOrgus(
    useShallow((s) => ({
      ready: s.phase === 'ready',
      mock: s.mode === 'mock',
      hasPolicy: !!s.ext.policy,
      needPolicy: !!s.ext.policy && !(s.ext.policy as PolicyState).config,
      needSecrets: !s.ext.secrets,
    })),
  );
  useEffect(() => {
    if (!mock || !ready) return;
    const st = useOrgus.getState();
    const now = Date.now();
    const projectIds = Object.keys(st.projects);
    const sessions = Object.values(st.sessions);
    if (hasPolicy && needPolicy) {
      const pol = st.ext.policy as PolicyState;
      const config = mockConfig(projectIds, now);
      mockLog = mockChanges(pol.rules, config, now);
      setExt('policy', { ...pol, version: mockLog.length, config, frozen: frozenOf(config, now), modelSwitches: mockModelSwitches(sessions, now) } satisfies PolicyState);
    }
    if (needSecrets) setExt('secrets', mockSecrets(projectIds, Object.keys(st.machines), sessions, now));
  }, [mock, ready, hasPolicy, needPolicy, needSecrets]);
}

export interface PolicyView {
  phase: 'loading' | 'error' | 'missing' | 'ready';
  policy: PolicyState | null;
  config: PolicyConfig;
  mock: boolean;
}

export function usePolicyV2(): PolicyView {
  const v = useApprovals();
  useMockSeedV2();
  const config = normalizeConfig(v.policy?.config);
  if (v.phase === 'ready' && !v.policy) return { phase: v.mock ? 'loading' : 'missing', policy: null, config, mock: v.mock };
  if (v.phase === 'ready' && v.mock && !v.policy?.config) return { phase: 'loading', policy: v.policy, config, mock: v.mock };
  return { phase: v.phase, policy: v.policy, config, mock: v.mock };
}

export interface SecretsView {
  phase: 'loading' | 'error' | 'missing' | 'ready';
  secrets: SecretsState | null;
  mock: boolean;
}

export function useSecrets(): SecretsView {
  useMockSeedV2();
  const { phase, mode, secrets } = useOrgus(useShallow((s) => ({ phase: s.phase, mode: s.mode, secrets: (s.ext.secrets as SecretsState | undefined) ?? null })));
  const mock = mode === 'mock';
  if (phase === 'loading' || phase === 'error') return { phase, secrets, mock };
  if (!secrets) return { phase: mock ? 'loading' : 'missing', secrets, mock };
  return { phase: 'ready', secrets, mock };
}

// ── Settings, freezes ─────────────────────────────────────────────────────────

function mockCommit(change: Omit<PolicyChange, 'version' | 'at' | 'by'>, next: Partial<PolicyState>): number {
  const pol = policyNow()!;
  const at = Date.now();
  const version = pol.version + 1;
  const full: PolicyChange = { ...change, version, at, by: 'you' };
  full.summary = summarizeChange(full);
  mockLog = [...(mockLog ?? []), full];
  const config = next.config ?? pol.config ?? normalizeConfig(null);
  setExt('policy', { ...pol, ...next, version, updatedAt: at, frozen: frozenOf(config, at) });
  return version;
}

/** The config with one settings layer replaced (an empty project layer is removed). */
export function withLayer(config: PolicyConfig, scope: PolicyScope, settings: PolicySettings): PolicyConfig {
  const before = normalizeConfig(config);
  const next: PolicyConfig = { ...before, projects: { ...before.projects } };
  if (scope.kind === 'org') next.org = settings;
  else if (Object.keys(settings).length) next.projects[scope.projectId] = settings;
  else delete next.projects[scope.projectId];
  return next;
}

/** Save one settings layer. Resolves with the launches the saved layers now refuse, if any. */
export async function saveSettings(scope: PolicyScope, settings: PolicySettings): Promise<LayerConflict[]> {
  if (isMock()) {
    await wait(300);
    const pol = policyNow()!;
    const before = normalizeConfig(pol.config);
    const config = withLayer(before, scope, settings);
    mockCommit({ kind: 'settings', ruleId: '', configBefore: before, configAfter: config }, { config });
    return layerConflicts(pol.rules, config, scope);
  }
  const r = await api<{ warnings?: LayerConflict[] }>('/api/policy/settings', { method: 'PUT', body: { scope, settings } });
  return Array.isArray(r?.warnings) ? r.warnings : [];
}

export async function saveFreeze(input: FreezeWindowInput, id?: string): Promise<FreezeWindow> {
  if (isMock()) {
    await wait(300);
    const pol = policyNow()!;
    const before = normalizeConfig(pol.config);
    const window: FreezeWindow = { ...input, id: id ?? `frz_DEMO${Date.now().toString(36).toUpperCase()}`, createdBy: 'you', createdAt: Date.now() };
    const config: PolicyConfig = { ...before, freezes: id ? before.freezes.map((w) => (w.id === id ? window : w)) : [...before.freezes, window] };
    mockCommit({ kind: 'freeze', ruleId: '', configBefore: before, configAfter: config, summary: `${id ? 'Changed' : 'Added'} freeze "${window.name}"` }, { config });
    return window;
  }
  const res = await api<{ freeze: FreezeWindow }>(id ? `/api/policy/freezes/${encodeURIComponent(id)}` : '/api/policy/freezes', { method: id ? 'PUT' : 'POST', body: input });
  return res.freeze;
}

export async function deleteFreeze(w: FreezeWindow): Promise<void> {
  if (isMock()) {
    await wait(300);
    const before = normalizeConfig(policyNow()!.config);
    const config = { ...before, freezes: before.freezes.filter((x) => x.id !== w.id) };
    mockCommit({ kind: 'freeze', ruleId: '', configBefore: before, configAfter: config, summary: `Deleted freeze "${w.name}"` }, { config });
    return;
  }
  await api(`/api/policy/freezes/${encodeURIComponent(w.id)}`, { method: 'DELETE' });
}

// ── Effective settings, launches ─────────────────────────────────────────────

export async function fetchEffective(projectId: Id | null, session: { model?: string; permissionMode?: string } = {}): Promise<EffectiveLaunch> {
  if (isMock()) {
    await wait(150);
    const pol = policyNow()!;
    return effectiveLaunch(pol.rules, normalizeConfig(pol.config), projectId, session);
  }
  const q = new URLSearchParams();
  if (projectId) q.set('projectId', projectId);
  if (session.model) q.set('model', session.model);
  if (session.permissionMode) q.set('permissionMode', session.permissionMode);
  return api<EffectiveLaunch>(`/api/policy/effective?${q}`);
}

export async function fetchLaunches(sessionId: Id): Promise<LaunchRecord[]> {
  if (isMock()) return [];
  return (await api<{ launches: LaunchRecord[] }>(`/api/policy/launches?sessionId=${encodeURIComponent(sessionId)}`)).launches;
}

// ── Versions ─────────────────────────────────────────────────────────────────

export async function fetchVersions(): Promise<PolicyVersionInfo[]> {
  if (isMock()) {
    await wait(200);
    return [...(mockLog ?? [])].reverse().map((c) => ({ version: c.version, at: c.at, by: c.by, kind: c.kind, summary: c.summary ?? summarizeChange(c), ...(c.rollbackOf !== undefined ? { rollbackOf: c.rollbackOf } : {}) }));
  }
  return (await api<{ versions: PolicyVersionInfo[] }>('/api/policy/versions?limit=200')).versions;
}

function mockSnapshot(version: number): PolicySnapshot {
  const pol = policyNow()!;
  if (version === pol.version) return { version, rules: pol.rules, config: normalizeConfig(pol.config) };
  return snapshotAt(mockLog ?? [], version);
}

export async function fetchDiff(from: number, to: number): Promise<PolicyDiff> {
  if (isMock()) {
    await wait(150);
    return { from, to, entries: diffSnapshots(mockSnapshot(from), mockSnapshot(to)) };
  }
  return api<PolicyDiff>(`/api/policy/diff?from=${from}&to=${to}`);
}

/** What the last 7 days would have looked like under an earlier version's rules. */
export async function simulateVersion(version: number): Promise<PolicySimulationResult> {
  if (isMock()) return simulate({ rules: mockSnapshot(version).rules });
  return apiPost<PolicySimulationResult>('/api/policy/simulate', { version });
}

export async function rollback(version: number): Promise<{ version: number }> {
  if (isMock()) {
    await wait(350);
    const pol = policyNow()!;
    const target = mockSnapshot(version);
    const before = normalizeConfig(pol.config);
    const v = mockCommit({ kind: 'rollback', ruleId: '', rules: target.rules, configBefore: before, configAfter: target.config, rollbackOf: version, summary: `Rolled back to version ${version}` }, { rules: target.rules, config: target.config });
    return { version: v };
  }
  return apiPost<{ version: number }>('/api/policy/rollback', { version });
}

// ── Secrets ──────────────────────────────────────────────────────────────────

const secretsNow = () => useOrgus.getState().ext.secrets as SecretsState | undefined;

export async function saveSecret(input: SecretInput, id?: string): Promise<SecretMeta> {
  if (isMock()) {
    await wait(300);
    const cur = secretsNow()!;
    const now = Date.now();
    const prev = id ? cur.secrets.find((s) => s.id === id) : undefined;
    if (!prev && cur.secrets.some((s) => s.name === input.name && s.scope.kind === input.scope.kind)) throw new Error(`A secret named ${input.name} already exists for that scope.`);
    const meta: SecretMeta = prev
      ? { ...prev, name: input.name, scope: input.scope, ...(input.description !== undefined ? { description: input.description } : {}), updatedBy: 'you', updatedAt: now, valueVersion: prev.valueVersion + (input.value ? 1 : 0) }
      : { id: `sec_DEMO${now.toString(36).toUpperCase()}`, name: input.name, scope: input.scope, ...(input.description ? { description: input.description } : {}), createdBy: 'you', createdAt: now, valueVersion: 1, leaseCount: 0 };
    setExt('secrets', { ...cur, secrets: prev ? cur.secrets.map((s) => (s.id === id ? meta : s)) : [...cur.secrets, meta] });
    return meta;
  }
  const res = await api<{ secret: SecretMeta }>(id ? `/api/secrets/${encodeURIComponent(id)}` : '/api/secrets', { method: id ? 'PUT' : 'POST', body: input });
  return res.secret;
}

export async function deleteSecret(id: string): Promise<void> {
  if (isMock()) {
    await wait(300);
    const cur = secretsNow()!;
    setExt('secrets', { ...cur, secrets: cur.secrets.filter((s) => s.id !== id) });
    return;
  }
  await api(`/api/secrets/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function revokeLease(id: string): Promise<SecretLease> {
  if (isMock()) {
    await wait(300);
    const cur = secretsNow()!;
    const l = cur.leases.find((x) => x.id === id)!;
    const ended: SecretLease = { ...l, state: 'revoked', endedAt: Date.now(), endReason: 'Revoked by you.', endedBy: 'you' };
    setExt('secrets', { ...cur, leases: cur.leases.map((x) => (x.id === id ? ended : x)) });
    return ended;
  }
  return (await apiPost<{ lease: SecretLease }>(`/api/secrets/leases/${encodeURIComponent(id)}/revoke`, {})).lease;
}

export async function rotateDataKey(): Promise<void> {
  if (isMock()) {
    await wait(400);
    const cur = secretsNow()!;
    setExt('secrets', { ...cur, keyring: { ...cur.keyring, dataKeyRotatedAt: Date.now() } });
    return;
  }
  await apiPost('/api/secrets/rotate', {});
}
